import { OrganizationTemplate } from '@norma/shared/types/organization.type';
import { Document, Packer } from 'docx';
import DocxMerger from 'docx-merger';
import docx4js from 'docx4js';
import { useAppDispatch } from 'src/hooks/store';
import { fetchOrganization, getTemplateFile } from 'src/slices/organization';

const getSelectedTemplate = (
  templates: OrganizationTemplate[] | undefined,
  type: 'cover' | 'body',
): OrganizationTemplate | undefined => {
  if (!templates) return undefined;
  return templates.find((tpl) => tpl.type === type && tpl.isSelected);
};

export const mergeWithBodyTemplate = async (
  organizationId: string | undefined,
  mergedDocBuffer: Uint8Array,
  dispatch: ReturnType<typeof useAppDispatch>,
): Promise<Uint8Array> => {
  if (!organizationId) return mergedDocBuffer;

  const organization = await dispatch(
    fetchOrganization(organizationId),
  ).unwrap();
  const template: OrganizationTemplate[] | undefined = organization.templates;

  if (!template || template.length === 0) {
    return mergedDocBuffer;
  }

  const bodyTemplate = getSelectedTemplate(template, 'body');
  if (!bodyTemplate) return mergedDocBuffer;

  const { url: signedUrl } = await dispatch(
    getTemplateFile({
      organizationId,
      storageName: bodyTemplate.storageName,
    }),
  ).unwrap();

  const generatedDoc = await docx4js.load(mergedDocBuffer);

  const responseTemplate = await fetch(signedUrl);
  if (!responseTemplate.ok) {
    const errorText = await responseTemplate.text();
    console.error('Erreur HTTP:', errorText);
    throw new Error(
      `Erreur lors du téléchargement du template: ${responseTemplate.status}`,
    );
  }

  const templateBuffer = await responseTemplate.arrayBuffer();

  const templateDoc = await docx4js.load(templateBuffer);

  const headerPart = templateDoc.parts['word/header1.xml'];
  if (headerPart) {
    generatedDoc.parts['word/header1.xml'] = headerPart;

    const headerRels = templateDoc.parts['word/_rels/header1.xml.rels'];
    if (headerRels) {
      generatedDoc.parts['word/_rels/header1.xml.rels'] = headerRels;
    }
  }

  const footerPart = templateDoc.parts['word/footer1.xml'];
  if (footerPart) {
    generatedDoc.parts['word/footer1.xml'] = footerPart;

    const footerRels = templateDoc.parts['word/_rels/footer1.xml.rels'];
    if (footerRels) {
      generatedDoc.parts['word/_rels/footer1.xml.rels'] = footerRels;
    }
  }

  Object.keys(templateDoc.parts).forEach((partName) => {
    if (
      partName.startsWith('word/header') ||
      partName.startsWith('word/footer')
    ) {
      generatedDoc.parts[partName] = templateDoc.parts[partName];
      const relsName = `word/_rels/${partName.split('/')[1]}.rels`;
      if (templateDoc.parts[relsName]) {
        generatedDoc.parts[relsName] = templateDoc.parts[relsName];
      }
    }
  });

  Object.keys(templateDoc.parts).forEach((partName) => {
    if (partName.startsWith('word/media/')) {
      generatedDoc.parts[partName] = templateDoc.parts[partName];
    }
  });

  const zip = generatedDoc.serialize();
  if (typeof zip.generate === 'function') {
    return zip.generate({ type: 'uint8array' });
  }
  throw new Error('Unknown output type from docx4js.serialize()');
};

export const mergeWithCoverTemplate = async (
  organizationId: string | undefined,
  document: Document,
  dispatch: ReturnType<typeof useAppDispatch>,
): Promise<Uint8Array> => {
  const bufferGenerated = await Packer.toBuffer(document);
  if (!organizationId) {
    return bufferGenerated;
  }

  const organization = await dispatch(
    fetchOrganization(organizationId),
  ).unwrap();
  const template: OrganizationTemplate[] | undefined = organization.templates;
  if (!template || template.length === 0) return bufferGenerated;

  const coverTemplate = getSelectedTemplate(template, 'cover');
  console.log('coverTemplate', coverTemplate?.filename);
  if (!coverTemplate) return bufferGenerated;

  const { url: signedUrl } = await dispatch(
    getTemplateFile({
      organizationId,
      storageName: coverTemplate.storageName,
    }),
  ).unwrap();

  const response = await fetch(signedUrl);
  if (!response.ok) throw new Error('Erreur lors du téléchargement du cover');
  const coverBuffer = new Uint8Array(await response.arrayBuffer());

  const merger = new DocxMerger({}, [coverBuffer, bufferGenerated]);

  return new Promise<Uint8Array>((resolve) => {
    merger.save('arraybuffer', (data: ArrayBuffer) => {
      console.log('Document avec cover généré');
      resolve(new Uint8Array(data));
    });
  });
};
